import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Crosshair, Save, Pencil, X } from "lucide-react";

export default function TodaysFocus() {
  const { user, updateSettings } = useAuth();
  const stored = user?.daily_focus || "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(stored);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(stored);
  }, [stored]);

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings({ daily_focus: draft });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(stored);
    setEditing(false);
  };

  const empty = !stored || stored.trim().length === 0;

  return (
    <div
      className="rounded-2xl border-2 border-primary/30 bg-card p-5 sm:p-6 shadow-sm relative overflow-hidden"
      data-testid="todays-focus"
      style={{
        backgroundImage:
          "radial-gradient(circle at 0% 100%, hsl(var(--primary) / 0.10), transparent 50%)",
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="size-9 rounded-xl bg-primary/15 flex items-center justify-center">
            <Crosshair strokeWidth={1.5} className="size-5 text-primary" />
          </div>
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Today's focus
          </div>
        </div>
        {!editing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(true)}
            data-testid="focus-edit-button"
            className="rounded-xl"
          >
            <Pencil strokeWidth={1.5} className="size-4 mr-1.5" />
            {empty ? "Set focus" : "Edit"}
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="What's the one thing you want to stay focused on today?"
            rows={3}
            autoFocus
            data-testid="focus-textarea"
            className="resize-y text-base"
          />
          <div className="flex items-center gap-2">
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-xl"
              data-testid="focus-save-button"
            >
              <Save strokeWidth={1.5} className="size-4 mr-1.5" />
              {saving ? "Saving…" : "Save focus"}
            </Button>
            <Button
              variant="ghost"
              onClick={cancel}
              className="rounded-xl"
              data-testid="focus-cancel-button"
            >
              <X strokeWidth={1.5} className="size-4 mr-1.5" />
              Cancel
            </Button>
          </div>
        </div>
      ) : empty ? (
        <p className="text-base text-muted-foreground">
          Write one thing you want to anchor your day around. It will greet you every time you open the app.
        </p>
      ) : (
        <blockquote
          className="font-display text-xl sm:text-2xl lg:text-3xl font-bold leading-snug tracking-tight whitespace-pre-wrap"
          data-testid="focus-display"
        >
          {stored}
        </blockquote>
      )}
    </div>
  );
}
